import React, {
  memo,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Disclosure } from "@headlessui/react";
import {
  BarsArrowUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/solid";

import { FilteredBLPFilters } from "../api/getFilteredBLP";
import { useGetBLPTableFilterOptions } from "../api/getBLPTableFilterOptions";
import RefreshButton from "../../../../components/RefreshButton";
import SearchButton from "../../../../components/SearchButton";
import SelectHookFormMulti from "../../../../components/SelectHookFormMulti";

interface SelectOption {
  label: string;
  value: string | number;
}

interface FilterInput extends FilteredBLPFilters {
  busLicensePermitType: SelectOption[];

  issuer: SelectOption[];

  licenseOrPermitNumber: SelectOption[];

  storeNumber: SelectOption[];
}

interface BusLicensesAndPermitsTableFilterProps {
  tableFilter: boolean;

  isSearching: boolean;

  onSearch: (
    filters: FilteredBLPFilters
  ) => Promise<void>;

  onRefresh: () => void;
}

const OLD_LICENSE_OPTIONS: SelectOption[] = [
  {
    label: "True",
    value: "true",
  },
  {
    label: "False",
    value: "false",
  },
];

const createInitialFilterInput =
  (): FilterInput => ({
    busLicensePermitTypeId: [],
    busLicensePermitType: [],

    issuerId: [],
    issuer: [],

    licenseOrPermitNumberId: [],
    licenseOrPermitNumber: [],

    storeNumberId: [],
    storeNumber: [],

    licenseStatusId: null,
    isOldLicense: null,
  });

const BusLicensesAndPermitsTableFilterComponent:
  React.FC<
    BusLicensesAndPermitsTableFilterProps
  > = ({
    tableFilter,
    isSearching,
    onSearch,
    onRefresh,
  }) => {
    const [
      filterInput,
      setFilterInput,
    ] = useState<FilterInput>(
      createInitialFilterInput
    );

    const {
      data: tableFilterOptions,
      isLoading: optionsLoading,
    } = useGetBLPTableFilterOptions();

    const typeOptions =
      useMemo<SelectOption[]>(() => {
        const types =
          tableFilterOptions?.types ?? [];

        if (!Array.isArray(types)) {
          return [];
        }

        return types
          .filter(
            (item) =>
              item.busLicensePermitTypeId !==
                null &&
              item.busLicensePermitTypeId !==
                undefined &&
              String(
                item.busLicensePermitTypeName ??
                  ""
              ).trim() !== ""
          )
          .map((item) => ({
            label: String(
              item.busLicensePermitTypeName
            ).trim(),

            value: Number(
              item.busLicensePermitTypeId
            ),
          }));
      }, [tableFilterOptions?.types]);

    const issuerOptions =
      useMemo<SelectOption[]>(() => {
        const issuers =
          tableFilterOptions?.issuers ?? [];

        if (!Array.isArray(issuers)) {
          return [];
        }

        return issuers
          .filter(
            (issuer) =>
              issuer.issuer !== null &&
              issuer.issuer !== undefined &&
              String(
                issuer.issuerName ?? ""
              ).trim() !== ""
          )
          .map((issuer) => ({
            label: String(
              issuer.issuerName
            ).trim(),

            value: Number(
              issuer.issuer
            ),
          }))
          .sort((first, second) =>
            first.label.localeCompare(
              second.label
            )
          );
      }, [tableFilterOptions?.issuers]);

    const licenseNumberOptions =
      useMemo<SelectOption[]>(() => {
        const licenseNumbers =
          tableFilterOptions
            ?.licenseOrPermitNumbers ??
          [];

        if (
          !Array.isArray(
            licenseNumbers
          )
        ) {
          return [];
        }

        return licenseNumbers
          .filter(
            (item) =>
              String(
                item.licenseOrPermitNumber ??
                  ""
              ).trim() !== ""
          )
          .map((item) => {
            const licenseOrPermitNumber =
              String(
                item.licenseOrPermitNumber
              ).trim();

            return {
              label:
                licenseOrPermitNumber,

              value:
                licenseOrPermitNumber,
            };
          });
      }, [
        tableFilterOptions
          ?.licenseOrPermitNumbers,
      ]);

      const storeNumberOptions =
          useMemo<SelectOption[]>(() => {
              const storeNumbers =
                  tableFilterOptions?.storeNumbers ?? [];
      
              if (
                  !Array.isArray(
                      storeNumbers
                  )
              ) {
          return [];
        }
      
        return storeNumbers
          .filter(
              (item) =>
                  item.storeNumber !==
                  null &&
                  item.storeNumber !==
                  undefined
          )
          .map((item) => ({
            label: String(item.storeNumber),
            value: String(item.storeNumber),
          }));
      }, [tableFilterOptions?.storeNumbers]);

    const licenseStatusOptions =
      useMemo<SelectOption[]>(() => {
        const licenseStatuses =
          tableFilterOptions
            ?.licenseStatuses ??
          [];

        if (
          !Array.isArray(
            licenseStatuses
          )
        ) {
          return [];
        }

        return licenseStatuses
          .filter(
            (status) =>
              status.licenseStatusId !==
                null &&
              status.licenseStatusId !==
                undefined &&
              String(
                status.licenseStatusName ??
                  ""
              ).trim() !== ""
          )
          .map((status) => ({
            label: String(
              status.licenseStatusName
            ).trim(),

            value: Number(
              status.licenseStatusId
            ),
          }));
      }, [
        tableFilterOptions
          ?.licenseStatuses,
      ]);

    const selectedLicenseStatus =
      useMemo<SelectOption | null>(() => {
        if (
          filterInput.licenseStatusId ===
          null
        ) {
          return null;
        }

        return (
          licenseStatusOptions.find(
            (option) =>
              Number(option.value) ===
              filterInput.licenseStatusId
          ) ?? null
        );
      }, [
        filterInput.licenseStatusId,
        licenseStatusOptions,
      ]);

    const selectedOldLicense =
      useMemo<SelectOption | null>(() => {
        if (
          filterInput.isOldLicense === null
        ) {
          return null;
        }

        return (
          OLD_LICENSE_OPTIONS.find(
            (option) =>
              String(option.value) ===
              String(
                filterInput.isOldLicense
              )
          ) ?? null
        );
      }, [filterInput.isOldLicense]);

    const handleTypeChange =
      useCallback(
        (
          selected:
            | SelectOption[]
            | null
        ) => {
          const values = selected ?? [];

          setFilterInput((current) => ({
            ...current,

            busLicensePermitTypeId:
              values.map((item) =>
                Number(item.value)
              ),

            busLicensePermitType: values,
          }));
        },
        []
      );

    const handleIssuerChange =
      useCallback(
        (
          selected:
            | SelectOption[]
            | null
        ) => {
          const values = selected ?? [];

          setFilterInput((current) => ({
            ...current,

            issuerId:
              values.map((item) =>
                Number(item.value)
              ),

            issuer: values,
          }));
        },
        []
      );

    const handleLicenseNumberChange =
      useCallback(
        (
          selected:
            | SelectOption[]
            | null
        ) => {
          const values = selected ?? [];

          setFilterInput((current) => ({
            ...current,

            licenseOrPermitNumberId:
              values.map((item) =>
                String(item.value)
              ),

            licenseOrPermitNumber:
              values,
          }));
        },
        []
      );

      const handleStoreNumberChange =
          useCallback(
              (
                  selected:
                      SelectOption[]
                      | null
              ) => {
          const values = selected ?? [];
      
          setFilterInput((current) => ({
              ...current,

              storeNumberId:
            values.map((item) => 
                String(item.value)

                  ),
            storeNumber: values,
          }));
        },
        []
      );

    const handleLicenseStatusChange =
      useCallback(
        (
          selected:
            | SelectOption
            | null
        ) => {
          setFilterInput((current) => ({
            ...current,

            licenseStatusId:
              selected === null
                ? null
                : Number(selected.value),
          }));
        },
        []
      );

    const handleOldLicenseChange =
      useCallback(
        (
          selected:
            | SelectOption
            | null
        ) => {
          setFilterInput((current) => ({
            ...current,

            isOldLicense:
              selected === null
                ? null
                : String(
                    selected.value
                  ) === "true",
          }));
        },
        []
      );

    const handleSearch =
      useCallback(async () => {
        if (isSearching) {
          return;
        }

        await onSearch({
          busLicensePermitTypeId:
            filterInput
              .busLicensePermitTypeId,

          issuerId:
            filterInput.issuerId,

          licenseOrPermitNumberId:
            filterInput
              .licenseOrPermitNumberId,

          storeNumberId:
            filterInput.storeNumberId,

          licenseStatusId:
            filterInput.licenseStatusId,

          isOldLicense:
            filterInput.isOldLicense,
        });
      }, [
        isSearching,
        onSearch,
        filterInput,
      ]);

    const refreshTable =
      useCallback(() => {
        setFilterInput(
          createInitialFilterInput()
        );

        onRefresh();
      }, [onRefresh]);

    return (
      <div className="space-y-2 px-4 pt-2 pl-2">
        <div className="pt-2">
          <BarsArrowUpIcon className="mb-1.5 h-6 w-6 text-primary" />

          <div className="-mt-9 ml-8 text-xl font-bold text-primary">
            Search Section
          </div>
        </div>

        <div className="w-full">
          <div className="mb-3">
            <Disclosure
              defaultOpen={tableFilter}
            >
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex w-full justify-between rounded-t-lg bg-gray-100 px-4 py-2 text-left text-sm font-medium text-primary hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75 sm:px-2">
                    <span>
                      Business License
                      &amp; Permit Details
                    </span>

                    <ChevronDownIcon
                      className={`${
                        open
                          ? "rotate-180 transform"
                          : ""
                      } h-5 w-5 text-gray-500`}
                    />
                  </Disclosure.Button>

                  <Disclosure.Panel className="rounded-b-lg bg-gray-100 text-sm text-primary">
                    <div className="bg-gray-100 p-3 sm:px-2 md:px-3">
                      <div className="pb-4 text-xs font-normal italic text-secondary">
                        <b>Note:</b>{" "}
                        License Type,
                        Issuer,
                        License/Permit
                        Number, and Store
                        Number allow
                        multiple selections.
                      </div>

                      <div className="flex flex-col gap-x-4 gap-y-2 text-xs">
                        <div className="w-full">
                          <label
                            htmlFor="blp-license-type"
                            className="mb-2 block font-medium text-gray-700"
                          >
                            License Type
                          </label>

                          <SelectHookFormMulti
                            inputId="blp-license-type"
                            options={
                              typeOptions
                            }
                            value={
                              filterInput.busLicensePermitType
                            }
                            onChange={
                              handleTypeChange
                            }
                            isLoading={
                              optionsLoading
                            }
                            isMulti={
                              true
                            }
                            secondDiv="items-center"
                          />
                        </div>

                        <div className="w-full">
                          <label
                            htmlFor="blp-issuer"
                            className="mb-2 block font-medium text-gray-700"
                          >
                            Issuer
                          </label>

                          <SelectHookFormMulti
                            inputId="blp-issuer"
                            options={
                              issuerOptions
                            }
                            value={
                              filterInput.issuer
                            }
                            onChange={
                              handleIssuerChange
                            }
                            isLoading={
                              optionsLoading
                            }
                            isMulti={
                              true
                            }
                            secondDiv="items-center"
                          />
                        </div>

                        <div className="w-full">
                          <label
                            htmlFor="blp-license-number"
                            className="mb-2 block font-medium text-gray-700"
                          >
                            License/Permit
                            Number
                          </label>

                          <SelectHookFormMulti
                            inputId="blp-license-number"
                            options={
                              licenseNumberOptions
                            }
                            value={
                              filterInput.licenseOrPermitNumber
                            }
                            onChange={
                              handleLicenseNumberChange
                            }
                            isLoading={
                              optionsLoading
                            }
                            isMulti={
                              true
                            }
                            secondDiv="items-center"
                          />
                        </div>

                        <div className="w-full">
                          <label
                            htmlFor="blp-store-number"
                            className="mb-2 block font-medium text-gray-700"
                          >
                            Store Number
                          </label>

                          <SelectHookFormMulti
                            inputId="blp-store-number"
                            options={
                              storeNumberOptions
                            }
                            value={
                              filterInput.storeNumber
                            }
                            onChange={
                              handleStoreNumberChange
                            }
                            isLoading={
                              optionsLoading
                            }
                            isMulti={
                              true
                            }
                            secondDiv="items-center"
                          />
                        </div>

                        <div className="w-full">
                          <label
                            htmlFor="blp-license-status"
                            className="mb-2 block font-medium text-gray-700"
                          >
                            License Status
                          </label>

                          <SelectHookFormMulti
                            inputId="blp-license-status"
                            options={
                              licenseStatusOptions
                            }
                            value={
                              selectedLicenseStatus
                            }
                            onChange={
                              handleLicenseStatusChange
                            }
                            isLoading={
                              optionsLoading
                            }
                            isMulti={
                              false
                            }
                            placeholder="Select..."
                            secondDiv="items-center"
                          />
                        </div>

                        <div className="w-full">
                          <label
                            htmlFor="blp-old-license"
                            className="mb-2 block font-medium text-gray-700"
                          >
                            Old License
                          </label>

                          <SelectHookFormMulti
                            inputId="blp-old-license"
                            options={
                              OLD_LICENSE_OPTIONS
                            }
                            value={
                              selectedOldLicense
                            }
                            onChange={
                              handleOldLicenseChange
                            }
                            isLoading={
                              false
                            }
                            isMulti={
                              false
                            }
                            placeholder="Select..."
                            secondDiv="items-center"
                          />
                        </div>

                        <div className="mb-12 mt-5 flex items-end justify-center gap-x-2 rounded-md md:ml-1 lg:ml-4">
                          <SearchButton
                            buttonValue={
                              isSearching
                                ? "Searching..."
                                : "Search"
                            }
                            onClick={
                              handleSearch
                            }
                          />

                          <RefreshButton
                            onClick={
                              refreshTable
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
        </div>
      </div>
    );
  };

export const BusLicensesAndPermitsTableFilter =
  memo(
    BusLicensesAndPermitsTableFilterComponent
  );